import { Route, type RoutesConfig } from 'mobx-router';

import Admin from 'components/Views/Admin/Admin';
import AnimationPreview from 'components/Views/Animation/AnimationPreview';
import Client from 'components/Views/Client/Client';
import Clients from 'components/Views/Clients/Clients';
import Dashboard from 'components/Views/Dashboard/Dashboard';
import Document from 'components/Views/Document/Document';
import Login from 'components/Views/Login';
import Profile from 'components/Views/Profile/Profile';
import Project from 'components/Views/Project/Project';
import Tearsheet from 'components/Views/Tearsheet/Tearsheet';
import {
    type AdCoordinate,
    type ClientCoordinate,
    type CreativeCoordinate,
    type ProjectCoordinate,
    type TearsheetAdCoordinate,
    type TearsheetCoordinate,
} from 'types/RouteTypes';

import root, { type RootStore } from './RootStore';

async function requireLogin() {
    if (root.user.authenticated) return true;
    await replaceRoute(routes.notAuthenticated);
    return false;
}

async function prepareAd(
    adCoordinate: AdCoordinate,
    filterSetName: string | undefined
) {
    // Called on both entry and params change: loadCreative reuses a creative
    // that's already loaded (or loading), and mobx-router doesn't await
    // onEnter, so an adID change mid-load is resolved there - see the note on
    // concurrent calls.
    await root.loadCreative(adCoordinate, {
        filterSetName,
        adID: adCoordinate.adID,
    });
}

async function prepareAnimationPreview(
    creativeCoordinate: CreativeCoordinate,
    file: string | undefined
) {
    if (file) {
        await root.loadAnimationPreview(creativeCoordinate, file);
    } else {
        await replaceRoute(routes.notFound);
    }
}

async function prepareCreative(
    creativeCoordinate: CreativeCoordinate,
    filterSetName: string | undefined
) {
    await root.loadCreativeOrAnimation(creativeCoordinate, { filterSetName });
}

async function prepareTearsheetAd(
    tearsheetAdCoordinate: TearsheetAdCoordinate
) {
    // Called on both entry and params change, for the same reasons as
    // prepareAd: loadTearsheet reuses a live tearsheet, and concurrent calls
    // await the same load and resume in order, so the latest adIndex wins.
    // The '-' redirect below comes back through here as a params change and
    // finds the tearsheet already loaded.
    const tearsheet = await root.loadTearsheet(tearsheetAdCoordinate);

    // Latest wins. If a navigation to a different tearsheet started while
    // this one loaded, it has reset this tearsheet - so `exists` below would
    // read false and redirect away from the tearsheet now being shown.
    if (tearsheet !== root.tearsheet) return;

    // A missing document still loads - status ends up LOADED - so `exists`
    // is the only signal. Tearsheet reads are public, so a missing one is
    // reported as missing rather than as a permission error.
    if (!tearsheet.exists) {
        return replaceRoute(routes.notFound);
    }

    // ":adIndex" is the ad's nominal1BasedIndex (see TearsheetAd), not a
    // position in any array. A dash means "the first ad", whose nominal index
    // can't be known until the ads have loaded.
    if (tearsheetAdCoordinate.adIndex === '-') {
        const firstAd = tearsheet.ads.first;
        if (firstAd) {
            await replaceRoute(routes.tearsheet, {
                ...tearsheetAdCoordinate,
                adIndex: firstAd.nominal1BasedIndex,
            });
        } else {
            await replaceRoute(routes.notFound);
        }
    } else {
        tearsheet.ads.setActiveByNominal1BasedIndex(
            Number(tearsheetAdCoordinate.adIndex)
        );
    }
}

async function prepareTearsheetRoot(tearsheetCoordinate: TearsheetCoordinate) {
    if (root.ui.isMobile) {
        // The mobile view shows either an ad or a list, so don't
        // redirect - just show the list
        const tearsheet = await root.loadTearsheet(tearsheetCoordinate);
        // See prepareTearsheetAd on both checks
        if (tearsheet !== root.tearsheet) return;
        if (!tearsheet.exists) {
            return replaceRoute(routes.notFound);
        }
    } else {
        // If not on mobile, the UI has room to show both the ad list
        // and the first ad itself, so bounce to the first-ad view
        await replaceRoute(routes.tearsheet, {
            ...tearsheetCoordinate,
            adIndex: '-',
        });
    }
}

async function prepareProject(projectCoordinate: ProjectCoordinate) {
    await root.loadProject(projectCoordinate);
}

async function prepareClient(clientCoordinate: ClientCoordinate) {
    await root.loadClient(clientCoordinate);
}

// How mobx-router 1.0.0 dispatches (node_modules/mobx-router/dist/router-store.js):
// - A navigation to the current path does nothing at all.
// - Changing route runs beforeExit, beforeEnter, onExit, then onEnter.
//   Changing only params or query on the same route runs onParamsChange
//   alone. onEnter and onParamsChange are mutually exclusive: a route without
//   onParamsChange ignores same-route navigations entirely.
// - Only beforeExit and beforeEnter are awaited, and either can cancel by
//   returning false. onEnter and onParamsChange are called and forgotten, so
//   a second navigation can start while an earlier handler is still awaiting.
const routes: RoutesConfig<RootStore> = {
    dashboard: new Route({
        path: '/dashboard',
        component: <Dashboard />,
        onEnter: async (route, params, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = false;
                // Passing `undefined` to `setCurrent` means 'There is no
                // document data to view'; the route is a non-document view.
                store.setCurrent(undefined);
                await store.user.starred.load();
            }
        },
    }),

    clients: new Route({
        path: '/clients',
        component: <Clients />,
        onEnter: async (route, params, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                store.loadClients();
            }
        },
    }),

    profile: new Route({
        path: '/profile',
        component: <Profile />,
        onEnter: async (route, params, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = false;
            }
        },
    }),

    ad: new Route({
        path: '/view/:clientID/:projectID/:creativeID/:adID',
        component: <Document />,
        onEnter: async (
            route,
            params: AdCoordinate,
            store,
            queryParams: { filterSetName?: string } | undefined
        ) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                await prepareAd(params, queryParams?.filterSetName);
            }
        },
        onParamsChange: async (route, params, store, queryParams) => {
            await prepareAd(params, queryParams?.filterSetName);
        },
    }),

    preview: new Route({
        path: '/preview/:clientID/:projectID/:creativeID',
        component: <AnimationPreview />,
        onEnter: async (
            route,
            params: CreativeCoordinate,
            store,
            queryParams: { file?: string } | undefined
        ) => {
            // Only logged-in users see these: logged out, DesktopLayout renders
            // SimpleHeader rather than NavHeader. 58a1c787 deliberately
            // restored breadcrumbs to this view (AnimationPreview.onLoad).
            store.ui.areBreadcrumbsVisible = true;
            await prepareAnimationPreview(params, queryParams?.file);
        },
        onParamsChange: async (route, params, store, queryParams) => {
            await prepareAnimationPreview(params, queryParams?.file);
        },
    }),

    creative: new Route({
        path: '/view/:clientID/:projectID/:creativeID',
        component: <Document />,
        onEnter: async (
            route,
            params: CreativeCoordinate,
            store,
            queryParams: { filterSetName?: string } | undefined
        ) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                await prepareCreative(params, queryParams?.filterSetName);
            }
        },
        onParamsChange: async (route, params, store, queryParams) => {
            await prepareCreative(params, queryParams?.filterSetName);
        },
    }),

    tearsheet: new Route({
        path: '/tearsheet/:clientID/:projectID/:creativeID/:tearsheetID/:adIndex',
        component: <Tearsheet />,
        onEnter: async (route, params: TearsheetAdCoordinate, store) => {
            store.ui.areBreadcrumbsVisible = true;
            await prepareTearsheetAd(params);
        },
        onParamsChange: async (route, params) => {
            await prepareTearsheetAd(params);
        },
    }),

    tearsheetRoot: new Route({
        path: '/tearsheet/:clientID/:projectID/:creativeID/:tearsheetID',
        component: <Tearsheet />,
        onEnter: async (route, params: TearsheetCoordinate, store) => {
            store.ui.areBreadcrumbsVisible = true;
            await prepareTearsheetRoot(params);
        },
        onParamsChange: async (route, params: TearsheetCoordinate) => {
            await prepareTearsheetRoot(params);
        },
    }),

    project: new Route({
        path: '/view/:clientID/:projectID',
        component: <Project />,
        onEnter: async (route, params: ProjectCoordinate, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                await prepareProject(params);
            }
        },
        onParamsChange: async (route, params: ProjectCoordinate) => {
            await prepareProject(params);
        },
    }),

    client: new Route({
        path: '/view/:clientID',
        component: <Client />,
        onEnter: async (route, params: ClientCoordinate, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                await prepareClient(params);
            }
        },
        onParamsChange: async (route, params: ClientCoordinate) => {
            await prepareClient(params);
        },
    }),

    admin: new Route({
        path: '/admin',
        component: <Admin />,
        onEnter: async (route, params, store) => {
            if (await requireLogin()) {
                store.ui.areBreadcrumbsVisible = true;
                store.admin.load();
                store.setCurrent(undefined);
            }
        },
    }),

    home: new Route({
        path: '/',
        component: <div>home</div>,
        onEnter: async () => {
            if (await requireLogin()) {
                await replaceRoute(routes.dashboard);
            }
        },
    }),

    notAuthenticated: new Route({
        path: '/login',
        component: <Login />,
        onEnter: async (route, params, store) => {
            if (store.user.authenticated) {
                await replaceRoute(routes.clients);
            } else {
                store.ui.areBreadcrumbsVisible = false;
            }
        },
    }),

    notFound: new Route({
        path: '/*',
        component: <div>404</div>,
        onEnter: async () => {
            console.warn('routes: notFound');
            // FIXME: the 404 component is never shown - logged in lands on
            //  /clients and logged out on /login, with no explanation. This
            //  covers unknown URLs and missing documents (e.g. tearsheets)
            //  alike. Wants a real not-found view, or at least a message
            //  before redirecting.
            if (await requireLogin()) {
                await replaceRoute(routes.clients);
            }
        },
    }),
};

// Grab a reference to the known-good implementation of pushState.
// `replaceRoute` manipulates this, but is also asynchronous, so we want
// to be able to restore to it regardless of timing.
const realPushState = window.history.pushState;

async function replaceRoute(route: Route<any, any>, params?: any) {
    // This calls mobx-router#Router.goTo, but with
    // `window.history.replaceState` semantics instead of `.pushState` - the
    // correct behavior for guard/redirect navigations that shouldn't leave a
    // back-stack entry.
    //
    // mobx-router (1.0.0, unmaintained) has no "replace" navigation API: its
    // *only* History API interaction is window.history.pushState(), in
    // start-router's autorun. So we monkeypatch pushState -> replaceState
    // around goTo.
    //
    // When pushState is called: while no route has beforeExit/beforeEnter,
    // goTo runs synchronously in one MobX batch, and the autorun pushes when
    // the batch ends - before goTo even returns. A before* hook would make
    // goTo yield, moving the push to a later microtask. Holding the patch
    // until goTo settles covers both. onEnter/onParamsChange are not awaited
    // by goTo, so their async work does not extend the patch.
    //
    // The hazard is nesting, not concurrency. If an onEnter calls
    // replaceRoute synchronously (before its first await) during a navigation
    // that should push, both navigations share one batch, and the single
    // autorun push happens under this patch - so the outer push becomes a
    // replace and the page navigated from loses its history entry. Redirects
    // made after an await are unaffected.
    window.history.pushState = window.history.replaceState;

    try {
        await root.router.goTo(route, params ?? {}, {});
    } finally {
        // Restore regardless of outcome
        window.history.pushState = realPushState;
    }
}

export default routes;
